'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, TrendingUp, Flame, Sparkles, X } from 'lucide-react';
import { storage } from '@/lib/storage';
// SpearheadImpactToast is mounted globally in app/layout.tsx — no
// need to render it here. PostWorkoutImpact handles the OPMODE-side
// intel/level toast; Spearhead's territory toast is a separate flow.

interface WorkoutImpact {
  intelGained: number;
  leveledUp: boolean;
  newLevel?: number;
  newStreak?: number;
  breakdown?: {
    base: number;
    perExercise: number;
    assessmentBonus: number;
    dailyBonus: number;
    mysteryDrop: number;
    crossAppBonus?: number;
  };
  mysteryDrop: number;
  timestamp: number;
}

export default function PostWorkoutImpact() {
  const [impact, setImpact] = useState<WorkoutImpact | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = storage.get('postWorkoutImpact');
      if (!raw) return;

      const data: WorkoutImpact = JSON.parse(raw);
      // Only show if recent (within 2 minutes)
      if (Date.now() - data.timestamp > 120_000) {
        storage.remove('postWorkoutImpact');
        return;
      }

      setImpact(data);
      setVisible(true);
      // Clear so it doesn't show again on next load
      storage.remove('postWorkoutImpact');
    } catch { /* non-critical */ }
  }, []);

  const dismiss = () => setVisible(false);

  return (
    <>
      <AnimatePresence>
        {visible && impact && (
          <motion.div
            key="post-workout-impact"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8, transition: { duration: 0.18 } }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            className="relative rounded-xl overflow-hidden border-2 border-emerald-500/40 bg-gradient-to-r from-emerald-950/90 via-operator-slate to-operator-void shadow-lg shadow-emerald-500/10"
          >
            {/* Scan line effect */}
            <div
              className="absolute inset-0 opacity-[0.03] pointer-events-none"
              style={{
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
              }}
            />

            <div className="relative px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <motion.div
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 18, delay: 0.08 }}
                    className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0"
                  >
                    <Zap className="w-5 h-5 text-emerald-400" />
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <motion.div
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.12, duration: 0.22 }}
                      className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-400 mb-0.5"
                    >
                      Session Complete
                    </motion.div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 16, delay: 0.2 }}
                        className="text-lg font-heading font-bold text-white"
                      >
                        +{impact.intelGained} Intel
                      </motion.span>
                      {impact.mysteryDrop > 0 && (
                        <motion.span
                          initial={{ opacity: 0, scale: 0.4 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ type: 'spring', stiffness: 420, damping: 14, delay: 0.42 }}
                          className="flex items-center gap-1 text-xs text-amber-400 font-bold animate-pulse"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          Mystery Drop!
                        </motion.span>
                      )}
                    </div>
                  </div>
                </div>
                <button onClick={dismiss} className="text-gray-500 hover:text-gray-300 transition p-1" aria-label="Dismiss">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Details row — children stagger in left-to-right */}
              <motion.div
                className="flex items-center gap-4 mt-2 text-xs text-gray-400"
                initial="hidden"
                animate="show"
                variants={{ show: { transition: { staggerChildren: 0.08, delayChildren: 0.32 } } }}
              >
                {impact.leveledUp && impact.newLevel && (
                  <motion.div
                    variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}
                    className="flex items-center gap-1 text-blue-400 font-bold"
                  >
                    <TrendingUp className="w-3.5 h-3.5" />
                    Level {impact.newLevel}!
                  </motion.div>
                )}
                {impact.newStreak && impact.newStreak > 1 && (
                  <motion.div
                    variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}
                    className="flex items-center gap-1 text-orange-400"
                  >
                    <Flame className="w-3.5 h-3.5" />
                    {impact.newStreak} day streak
                  </motion.div>
                )}
                {impact.breakdown && (
                  <motion.div
                    variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}
                    className="flex items-center gap-2 text-[10px] text-gray-500"
                  >
                    <span>Base {impact.breakdown.base}</span>
                    {impact.breakdown.perExercise > 0 && <span>+{impact.breakdown.perExercise} exercises</span>}
                    {impact.breakdown.dailyBonus > 0 && <span>+{impact.breakdown.dailyBonus} daily</span>}
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
